// Record a meeting (SRecord, SPhoneRecord): choose the meeting (or a new one on a track), record in the room with
// a live transcript and live notes, Mark moment, Pause, Stop; then the assistant writes the minutes as a card to
// review and file. "Send a notetaker" is a later phase (shown, not active). Nothing is filed until a person approves.
// 6.3 (Record.dc.html, meetings/review.tsx): on the desktop, the steps 1 Record › 2 Process › 3 Review at the top; the
// recording itself is as it was; Process lists where the minutes job is (from the server); Review shows the draft
// minutes on the page, to check and send to Requests for approval; /record?recording=id opens a stopped recording
// there. The phone page is unchanged.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { SPEECH_LANGUAGE_LABELS, type MeetingRow, type Recording, type SpaceDetail } from '@dpw/shared';
import { AnimatePresence } from 'motion/react';
import { api } from '../api';
import { usePhone } from '../lib/media';
import { useMe, usePrefs, useSpaces, useVoiceStatus } from '../lib/queries';
import { SpaceSheet, createsSpaces } from '../ui/SpaceForm';
import { canRecord } from '../lib/recorder';
import { useInboundAddress } from '../ui/email';
import { clock, speakerColor, speakerView, Speakers, StageWave } from '../meetings/parts';
import { useMeetingRecorder } from '../meetings/useMeetingRecorder';
import { Title, TopBar } from '../phone/kit';
import { ProcessList, ReviewStep, StepHead } from '../meetings/review';

const NEW = 'new';
/** Shown instead of recording when the server has no voice service (no ELEVENLABS_API_KEY). */
const VOICE_OFF = 'Recording is off: the voice service is not connected on this server. An admin adds the ElevenLabs key to the server settings (see the README, Voice). Meanwhile, log the meeting by hand.';
function VoiceOff() {
  return (
    <div className="voiceoff" role="alert">
      <span>{VOICE_OFF}</span>
      <Link className="wbt" to="/meetings?log=1">Log a meeting by hand</Link>
    </div>
  );
}
const STEPS = ['Transcribing and separating speakers', 'Finding decisions and actions', 'Matching actions to spaces and tracks', 'Drafting the minutes'];

/** Meetings to record now: today's, nearest first (and the one in the address); the tracks a new one can go on. */
function useTargets() {
  const meetings = useQuery({ queryKey: ['meetings', 'all', null], queryFn: () => api.meetings.list({ when: 'all' }) });
  const spaces = useSpaces();
  const details = useQueries({ queries: (spaces.data ?? []).map(s => ({ queryKey: ['space', s.id], queryFn: () => api.spaces.get(s.id), staleTime: 60_000 })) });
  const loadedSpaces = details.map(d => d.data).filter((d): d is SpaceDetail => !!d);
  const tracks = loadedSpaces.flatMap(d => d.tracks.map(t => ({ id: t.id, label: `${d.name} · ${t.name}` })));
  const today = (meetings.data ?? []).filter(m => m.today || (!m.past && m.startsAt && new Date(m.startsAt).getTime() - Date.now() < 36 * 3600_000));
  const loaded = !!meetings.data && !!spaces.data && details.every(d => !!d.data);
  return { today, all: meetings.data ?? [], tracks, spaces: spaces.data ?? [], loaded };
}

/**
 * The meeting to record into, chosen for you: the one in the address, else today's first, else a new one (with the
 * only track when there is just one). Before release 5.2 an empty server (no meetings) left this empty, and the record
 * button stayed off with nothing saying why.
 */
function useDefaultTarget(t: ReturnType<typeof useTargets>, target: string, setTarget: (v: string) => void, trackId: string, setTrackId: (v: string) => void) {
  useEffect(() => {
    if (target || !t.loaded) return;
    setTarget(t.today[0]?.id ?? NEW);
  }, [target, t.loaded, t.today, setTarget]);
  useEffect(() => { if (target === NEW && !trackId && t.tracks.length === 1) setTrackId(t.tracks[0]!.id); }, [target, trackId, t.tracks, setTrackId]);
}

/**
 * Why the record button cannot start, in plain words, with the way forward. A recording files its minutes on a
 * meeting in a track, so with no space or no track there is nothing to record into yet: create one first (decided for
 * 5.2 over recording into "no track" and filing later, which would need an unfiled state the rest of the app does not
 * have).
 */
function Blocker({ t, target, trackId, phone }: { t: ReturnType<typeof useTargets>; target: string; trackId: string; phone?: boolean }) {
  const me = useMe().data;
  const [creating, setCreating] = useState(false);
  if (!t.loaded) return null;
  const btn = phone ? 'ph-btf sec' : 'wbt';
  let body: ReactNode = null;
  if (!t.spaces.length) {
    body = <>
      <span>There is nothing to record into yet: the minutes go on a meeting in a space and track, and there are no spaces.</span>
      {me && createsSpaces(me) ? <button type="button" className={btn} onClick={() => setCreating(true)}>Create a space first</button>
        : <span>Ask the director to add you to a space.</span>}
    </>;
  } else if (!t.tracks.length) {
    const sp = t.spaces[0]!;
    body = <>
      <span>Add a track first: the minutes go on a meeting in a track, and {t.spaces.length === 1 ? sp.name : 'your spaces'} {t.spaces.length === 1 ? 'has' : 'have'} none yet.</span>
      <Link className={btn} to={`/spaces/${sp.id}`}>Open {sp.name}</Link>
    </>;
  } else if (target === NEW && !trackId) body = <span>Choose the track this meeting belongs to, then record.</span>;
  if (!body) return null;
  return (
    <div className="voiceoff rblock" role="status">
      {body}
      <AnimatePresence>{creating && me && <SpaceSheet key="new-space" me={me} onClose={() => setCreating(false)} />}</AnimatePresence>
    </div>
  );
}

/** What the voice service listens for: the person's language (Settings, Voice). */
function LangChip() {
  const lang = usePrefs().data?.voice.language ?? 'eng';
  return <Link className="chip" to="/settings" title="Change it in Settings, Voice">Listening for {SPEECH_LANGUAGE_LABELS[lang]}</Link>;
}

/** The recording, polled while it runs and while the minutes are written. */
function useLive(rec: Recording | null, setRec: (r: Recording) => void) {
  const q = useQuery({
    queryKey: ['recording', rec?.id], queryFn: () => api.recordings.get(rec!.id), enabled: !!rec,
    refetchInterval: r => (r.state.data?.status === 'ready' || r.state.data?.status === 'failed' ? false : 4000),
  });
  useEffect(() => { if (q.data) setRec(q.data); }, [q.data, setRec]);
  // The minutes card was made on the server: the lists and counts that show cards learn about it now.
  const qc = useQueryClient();
  const card = q.data?.actionId ?? null;
  useEffect(() => {
    if (card) for (const k of ['actions', 'summary', 'notifications', 'today', 'meetings', 'meeting']) void qc.invalidateQueries({ queryKey: [k] });
  }, [card, qc]);
  return q.data ?? rec;
}

export function RecordPage() {
  return usePhone() ? <PhoneRecord /> : <DesktopRecord />;
}

function Target({ value, onChange, title, onTitle, trackId, onTrack, disabled }: {
  value: string; onChange: (v: string) => void; title: string; onTitle: (v: string) => void; trackId: string; onTrack: (v: string) => void; disabled: boolean;
}) {
  const t = useTargets();
  const listed = useMemo(() => {
    const extra = value && value !== NEW && !t.today.some(m => m.id === value) ? t.all.filter(m => m.id === value) : [];
    return [...extra, ...t.today];
  }, [t.today, t.all, value]);
  return (
    <>
      <label className="chip"><span className="d" /><span className="sr">Meeting</span>
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled} aria-label="Meeting">
          {listed.map((m: MeetingRow) => <option key={m.id} value={m.id}>{m.title}</option>)}
          <option value={NEW}>New activity from this recording</option>
        </select>
      </label>
      {value === NEW && t.tracks.length > 0 && <>
        <label className="chip"><select value={trackId} onChange={e => onTrack(e.target.value)} disabled={disabled} aria-label="Track">
          <option value="">Choose a track</option>{t.tracks.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
        </select></label>
        <label className="chip"><input value={title} onChange={e => onTitle(e.target.value)} disabled={disabled} placeholder="Meeting title" aria-label="Meeting title" /></label>
      </>}
    </>
  );
}

function DesktopRecord() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const r = useMeetingRecorder();
  const t = useTargets();
  const [tab, setTab] = useState<'app' | 'bot'>('app');
  const [target, setTarget] = useState(params.get('activity') ?? '');
  const [title, setTitle] = useState('');
  const [trackId, setTrackId] = useState('');
  useDefaultTarget(t, target, setTarget, trackId, setTrackId);
  const live = useLive(r.rec, r.setRec);
  const running = r.state === 'recording' || r.state === 'paused';
  const working = r.state === 'sending' || (r.state === 'done' && live?.status === 'processing');
  const ready = r.state === 'done' && live?.status === 'ready';
  const failed = r.state === 'done' && live?.status === 'failed';
  const [note, setNote] = useState('');
  // ?recording=id: a recording that has stopped opens at Process or Review (a link from Meetings or a notification).
  const reopen = params.get('recording');
  const { resume } = r;
  useEffect(() => {
    if (!reopen) return;
    let on = true;
    api.recordings.get(reopen).then(x => { if (on) resume(x); }, () => undefined);
    return () => { on = false; };
  }, [reopen, resume]);
  const voice = useVoiceStatus().data;
  const off = voice?.transcribe === false;
  const canGo = !off && !!voice && canRecord() && !!target && (target !== NEW || !!trackId);
  const start = () => { setNote(''); void r.start(target === NEW ? { trackId, title } : { activityId: target }); };
  const lines = live?.lines ?? [];
  const notes = live?.notes;
  const secs = running ? r.seconds : live?.durationSecs ?? r.seconds;
  const span = Math.max(3600, secs);
  const m = live?.minutes;
  /** Back to Record, for a new recording (and the address no longer names the old one). */
  const fresh = () => { r.reset(); if (reopen) setParams(p => { p.delete('recording'); return p; }, { replace: true }); };
  const stepNo = ready && m ? 3 : working || failed ? 2 : 1;
  return (
    <div className="rcd col" style={{ gap: 14 }}>
      <div className="hdr"><div className="htl"><h1>Record</h1><span className="xs mut">The assistant listens, takes notes live, and writes the minutes when you stop</span></div>
        <div className="hact">{tab === 'app' && <StepHead step={stepNo} />}<div className="seg2" role="group" aria-label="How"><button type="button" className={tab === 'app' ? 'on' : ''} aria-pressed={tab === 'app'} onClick={() => setTab('app')}>Record here</button>
          <button type="button" className={tab === 'bot' ? 'on' : ''} aria-pressed={tab === 'bot'} onClick={() => setTab('bot')}>Send a notetaker</button></div></div>
      </div>
      {note && tab === 'app' && r.state === 'idle' && <div className="okmsg" role="status">{note}</div>}
      {tab === 'bot' ? <Notetaker /> : ready && m && live ? <ReviewStep rec={live} onAnother={() => { setNote(''); fresh(); }}
        onDiscarded={() => { fresh(); setNote('Minutes discarded. The recording and its transcript stay with the meeting.'); }} /> : (
        <div className="studio">
          <section className="stage" aria-label="Recording">
            <div className="top">
              <Target value={target} onChange={setTarget} title={title} onTitle={setTitle} trackId={trackId} onTrack={setTrackId} disabled={r.state !== 'idle'} />
              <LangChip />
              {running && <span className="live" role="status"><i />LIVE</span>}
            </div>
            <div className="center">
              <div className="sub" role="status">{off ? 'Recording is off' : r.state === 'idle' || r.state === 'asking' ? 'Ready when you are' : r.state === 'paused' ? 'Paused' : running ? 'Listening and taking notes' : working ? 'Thinking' : 'Done'}</div>
              <div className="clock" aria-label={`Recorded ${clock(secs)}`}>{clock(secs)}</div>
              <StageWave bars={r.bars} level={r.level} on={r.state === 'recording'} />
              <div className="ctl">
                {running && <button type="button" className="gbt" onClick={r.mark}>Mark moment</button>}
                {r.state === 'idle' || r.state === 'asking'
                  ? <button type="button" className="recb" onClick={start} disabled={!canGo || r.state === 'asking'} aria-label="Start recording"><b /></button>
                  : running ? <button type="button" className="recb stop" onClick={() => void r.stop(r.marks)} aria-label="Stop and write the minutes"><b /></button> : null}
                {running && <button type="button" className="gbt" onClick={r.pause}>{r.state === 'paused' ? 'Resume' : 'Pause'}</button>}
              </div>
              {r.state === 'idle' && !off && <Blocker t={t} target={target} trackId={trackId} />}
              {r.state === 'idle' && !off && <div className="hint">Tell people in the room you are recording. Audio stays with the meeting.</div>}
              {off && r.state === 'idle' && <VoiceOff />}
              {running && <div className="hint">Keeps recording if you switch tabs. On a phone, keep the screen on. <button type="button" className="gbt" style={{ height: 30, marginLeft: 6 }} onClick={() => void r.discard()}>Discard</button></div>}
              {!canRecord() && <div className="err">This browser cannot record. Use Chrome, Edge or Safari, or log the meeting by hand.</div>}
              {r.error && <div className="err" role="alert">{r.error}</div>}
            </div>
            <div>
              <div className="tline" aria-hidden="true"><div className="bar"><i style={{ transform: `scaleX(${Math.min(1, secs / span)})` }} /></div>
                {(notes?.topics ?? []).map(([at], i) => <span key={`t${at}`} className="mk topic" style={{ left: `${(at / span) * 100}%` }}>{i + 1}</span>)}
                {r.marks.map(at => <span key={`f${at}`} className="mk flag" style={{ left: `${(at / span) * 100}%` }}>!</span>)}
              </div>
              <div className="tlabels"><span>00:00</span><span>{(notes?.topics.length ?? 0) ? `${notes!.topics.length} topics` : 'Topics and moments appear here as they happen'}</span><span>{clock(span)}</span></div>
            </div>
            {(working || failed) && (
              <div className="writing">
                {working && <><div className="sub">Writing the minutes</div><ProcessList rec={live} sending={r.state === 'sending'} /></>}
                {failed && <div className="done2" role="alert"><h3>The minutes could not be written</h3><div className="sub">{live?.error} The recording and transcript are kept.</div>
                  <div className="ctl">{live?.activityId && <button type="button" className="gbt" onClick={() => nav(`/meetings/${live.activityId}`)}>Open in Meetings</button>}<button type="button" className="gbt" onClick={r.reset}>Record another</button></div></div>}
              </div>
            )}
          </section>
          <LiveNotes rec={live} lines={lines} />
        </div>
      )}
    </div>
  );
}

/** Steps advance while the minutes are written (the last one stays until they are ready). */
function useStep(on: boolean) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!on) { setStep(0); return; }
    const t = window.setInterval(() => setStep(s => Math.min(STEPS.length - 1, s + 1)), 1600);
    return () => window.clearInterval(t);
  }, [on]);
  return step;
}

function LiveNotes({ rec, lines }: { rec: Recording | null; lines: Recording['lines'] }) {
  const n = rec?.notes;
  const sections: [string, [number, string][], string][] = [
    ['Topics', n?.topics ?? [], 'Topics show up as the conversation moves.'], ['Decisions', n?.decisions ?? [], 'Listening for decisions.'],
    ['Actions', n?.actions ?? [], 'Listening for who will do what.'], ['Meetings to set up', n?.meetings ?? [], 'Listening for follow-up meetings.'],
  ];
  return (
    <aside className="panel notes" aria-label="Live notes">
      <div className="sh"><b>Live transcript</b><span>{lines.length} lines · speakers separated</span></div>
      {rec && <Speakers rec={rec} canName />}
      <div className="trans" role="list" aria-label="Live transcript">
        {[...lines].reverse().map((l, i) => {
          const v = speakerView(l.who, rec?.speakers ?? []);
          return <div className="tl2" role="listitem" key={`${l.at}-${i}`}><span className="w">{clock(l.at)}</span><span className={`who ${v.state}`} style={{ ['--sc' as string]: speakerColor(l.who) }}><i />{v.name}{v.state === 'suggested' ? '?' : ''}</span><span>{l.t}</span></div>;
        })}
        {!lines.length && <div className="nempty">The transcript appears here as people speak.</div>}
      </div>
      <div className="sh"><b>The assistant is taking notes</b></div>
      {sections.map(([label, items, wait]) => (
        <div className="nsec2" key={label} role="group" aria-label={label}>
          <span className="k">{label} · {items.length}</span>
          {items.map(([at, t]) => <div className="nit" key={`${at}-${t}`}><span className="ts">{clock(at)}</span><span>{t}</span></div>)}
          {!items.length && <div className="nempty">{wait}</div>}
        </div>
      ))}
    </aside>
  );
}

/** Send a notetaker (Recall.ai): a later phase. Shown as the prototype has it, not active. */
function Notetaker() {
  const addr = useInboundAddress();
  return (
    <div className="botcard" aria-label="Send a notetaker">
      <div className="rowc btwc"><b>Send a notetaker to a Teams, Zoom or Meet call</b><span className="pl">Later phase</span></div>
      <div className="botid"><span className="botav">DP</span><div><b>Delta Partners Notes</b><div className="xs" style={{ color: '#A9A3D6' }}>How it appears in the call. It never speaks and never poses as a person.</div></div></div>
      <div className="xs" style={{ color: '#A9A3D6' }}>Meeting link, or forward the invite to {addr ?? 'the log address'}. Uses Recall.ai. Not connected yet: record here for now, or log the meeting by hand.</div>
      <div><Link className="wbt" to="/meetings?log=1">Log a meeting by hand</Link></div>
    </div>
  );
}

function PhoneRecord() {
  const [params] = useSearchParams();
  const r = useMeetingRecorder();
  const t = useTargets();
  const [target, setTarget] = useState(params.get('activity') ?? '');
  const [trackId, setTrackId] = useState('');
  useDefaultTarget(t, target, setTarget, trackId, setTrackId);
  const live = useLive(r.rec, r.setRec);
  const running = r.state === 'recording' || r.state === 'paused';
  const working = r.state === 'sending' || (r.state === 'done' && live?.status === 'processing');
  const ready = r.state === 'done' && live?.status === 'ready';
  const step = useStep(working);
  const voice = useVoiceStatus().data;
  const off = voice?.transcribe === false;
  const canGo = !off && !!voice && canRecord() && !!target && (target !== NEW || !!trackId);
  const m = live?.minutes;
  return (
    <div className="ph-page phm rcd">
      <TopBar back={{ to: '/meetings', label: 'Meetings' }} />
      <Title title="Record" sub={live?.title ?? 'In the room'} />
      {r.state === 'idle' && t.loaded && !t.tracks.length && !off && <Blocker t={t} target={target} trackId={trackId} phone />}
      {r.state === 'idle' && (!t.loaded || t.tracks.length > 0) && <>
        <div className="ph-grp flat" role="group" aria-label="Link to a meeting">
          <label className="ph-row"><span className="ph-rt"><div className="t">Meeting</div>
            <select className="ph-inp" value={target} onChange={e => setTarget(e.target.value)} aria-label="Meeting">
              {t.today.map(x => <option key={x.id} value={x.id}>{x.title}</option>)}<option value={NEW}>New activity from this recording</option></select></span></label>
          {target === NEW && <label className="ph-row"><span className="ph-rt"><div className="t">Track</div>
            <select className="ph-inp" value={trackId} onChange={e => setTrackId(e.target.value)} aria-label="Track"><option value="">Choose a track</option>{t.tracks.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}</select></span></label>}
        </div>
        <div className="xs mut" style={{ padding: '8px 4px' }}>Minutes go to its track. Put the phone in the middle of the table. Tell everyone in the meeting it is recorded.</div>
      </>}
      <div className="ph-stage">
        <div className="recbig">
          <div className="sub" role="status">{running ? (r.state === 'paused' ? 'Paused' : 'Listening') : working ? 'Writing the minutes' : ready ? 'Minutes ready for review' : 'Tap to record'}</div>
          <div className="clock">{clock(running ? r.seconds : live?.durationSecs ?? r.seconds)}</div>
          <StageWave bars={r.bars} level={r.level} on={r.state === 'recording'} />
          {!running && !working && !ready && <button type="button" className="recb" onClick={() => void r.start(target === NEW ? { trackId } : { activityId: target })} disabled={!canGo || r.state === 'asking'} aria-label="Start recording"><b /></button>}
          {running && <div className="ctl"><button type="button" className="gbt" onClick={r.mark}>Mark</button>
            <button type="button" className="recb stop" onClick={() => void r.stop(r.marks)} aria-label="Stop and write the minutes"><b /></button>
            <button type="button" className="gbt" onClick={r.pause}>{r.state === 'paused' ? 'Resume' : 'Pause'}</button></div>}
          {running && <div className="hint">{r.marks.length} moments marked · keep the screen on</div>}
          {working && <div className="steps">{STEPS.map((s, i) => <div key={s} className={`step${i < step ? ' done' : i === step ? ' on' : ''}`}><span className="s">{i < step ? '✓' : ''}</span>{s}</div>)}</div>}
          {ready && m && <div className="done2"><div className="sub">{m.actions.length} actions · {m.setup.length} to set up · {m.next.length} next steps · {m.decisions.length} decisions</div>
            <div className="hint">Nothing is filed until you review.</div>
            {live?.actionId && <Link className="wbt" to={`/requests?id=${live.actionId}`}>Review minutes</Link>}
            {live?.activityId && <Link className="gbt" to={`/meetings/${live.activityId}`}>Open the meeting</Link>}</div>}
          {r.state === 'idle' && !off && t.tracks.length > 0 && <Blocker t={t} target={target} trackId={trackId} phone />}
          {r.error && <div className="err" role="alert">{r.error}</div>}
          {off && r.state === 'idle' && <VoiceOff />}
        </div>
      </div>
      {(running || working) && <LiveNotes rec={live} lines={live?.lines ?? []} />}
    </div>
  );
}
